import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UPDATE_CHECK_INTERVAL_MS } from "../pwa-constant";
import { ServiceWorkerSubscription } from "../pwa-types";
import { activateWaitingWorker, subscribeToServiceWorker } from "../pwa-utils";

export interface PwaUpdateState {
  /** A newer build is installed and waiting to take over. */
  isUpdateAvailable: boolean;
  /** Activates the waiting worker and reloads so the new build is served. */
  applyUpdate: () => void;
  /** Hides the prompt; the update is applied on the next full reload anyway. */
  dismissUpdate: () => void;
}

/**
 * Owns the service-worker lifecycle so the app decides *when* to swap builds:
 * a clinician mid-note must never be reloaded underneath.
 */
export const usePwaUpdate = (): PwaUpdateState => {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(
    null
  );
  const [isDismissed, setIsDismissed] = useState(false);
  const subscriptionRef = useRef<ServiceWorkerSubscription | null>(null);

  useEffect(() => {
    // The worker would cache-first the dev server's un-hashed modules.
    if (!import.meta.env.PROD) return;

    let isActive = true;
    let lastCheckedAt = Date.now();

    void subscribeToServiceWorker({
      onUpdateReady: (waiting) => {
        if (!isActive) return;
        setWaitingWorker(waiting);
        setIsDismissed(false);
      },
    }).then((subscription) => {
      if (!isActive) subscription?.unsubscribe();
      else subscriptionRef.current = subscription;
    });

    const checkWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastCheckedAt < UPDATE_CHECK_INTERVAL_MS) return;

      lastCheckedAt = Date.now();
      void subscriptionRef.current?.checkForUpdate();
    };
    document.addEventListener("visibilitychange", checkWhenVisible);

    return () => {
      isActive = false;
      document.removeEventListener("visibilitychange", checkWhenVisible);
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (waitingWorker) activateWaitingWorker(waitingWorker);
  }, [waitingWorker]);

  const dismissUpdate = useCallback(() => setIsDismissed(true), []);

  return useMemo(
    () => ({
      isUpdateAvailable: waitingWorker !== null && !isDismissed,
      applyUpdate,
      dismissUpdate,
    }),
    [waitingWorker, isDismissed, applyUpdate, dismissUpdate]
  );
};
